/**
 * Responsibility: describe the forest biome — its ground, its colours, and that it grows trees.
 *
 * Contains no Three.js and no DOM access. Emits plain tree descriptions; the render layer never learns that a tree differs
 * from a tower.
 *
 * ## Where the trees went
 *
 * Tree placement lives in `world/treeStands.js`. It moved there unchanged when the wooded lake country arrived and needed
 * the same woodland, for the same reason `rockColumns.js` is shared by three rock biomes: one authoritative rule beats two
 * copies that drift. The noise labels are parameterised by biome id, and this biome passes `'forest'`, so every forest in
 * every existing world is byte-identical to before the move.
 *
 * The design behind that placement — patchy stands rather than a canopy, and one owning tile per tree — is documented
 * there.
 */

import { createFbm, toUnitRange } from '../noise.js';
import { createTreeStands } from '../treeStands.js';
import { FOREST, PALETTE, FOREST_TREE_PALETTE } from '../../config.js';

export const forest = {
  id: 'forest',
  label: 'Forest',

  palette: PALETTE.forest,

  /**
   * Trees are drawn from their own gradient, not the ground's.
   *
   * The forest's terrain is deliberately muted so the trees carry the biome's identity. Sharing one gradient would leave
   * ground and trees the same colour and flatten the whole biome into a single green mass.
   */
  structurePalette: FOREST_TREE_PALETTE,

  suitabilityBias: FOREST.SUITABILITY_BIAS,

  /**
   * Build the ground elevation function.
   *
   * Forested hills: more relief than open ground, far less than the mountains. Enough shape to give the stands something to
   * drape over, not enough to make the trees look like they are clinging on.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    const field = createFbm(seed, 'elevation:forest', {
      octaves: FOREST.OCTAVES,
      baseFrequency: 1 / FOREST.WAVELENGTH,
      persistence: FOREST.PERSISTENCE,
      lacunarity: FOREST.LACUNARITY,
    });

    return function sampleForestElevation(x, z) {
      // Remapped to 0..1 so this biome never contributes a negative elevation to the blend.
      return toUnitRange(field(x, z)) * FOREST.MAX_ELEVATION;
    };
  },

  maxElevation: FOREST.MAX_ELEVATION,

  /**
   * Build the tree placement function.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    return createTreeStands(seed, 'forest');
  },
};
